import { WithSetter } from "@/components/custom/data-table/data-table-context";
import {
  ColumnDefTemplate,
  functionalUpdate,
  Updater,
} from "@tanstack/react-table";
import { type ClassValue, clsx } from "clsx";
import {
  DeepMap,
  DeepPartial,
  FieldPath,
  FieldPathValue,
  FieldValues,
  PathValue,
  SetValueConfig,
  useForm,
  UseFormReturn,
  useWatch,
} from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { isApiError } from "@/client/extend";
import { compareBuild, coerce } from "semver";
import { AxiosError } from "axios";
import { isAfter, isEqual } from "date-fns";
import React from "react";
import { CellContext } from "@tanstack/react-table";
import { UseQueryOptions } from "@tanstack/react-query";
import { HeaderContext } from "@tanstack/react-table";
import { MergeDeep, PartialDeep } from "type-fest";
import { Func } from "@/client/cancelable-promise";
import _ from "lodash";
import { useCurrentRef } from "@/hooks/use-current-ref";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type AsNumber<T> = T extends null | undefined
  ? T
  : T extends number
    ? number
    : number | undefined;

export const asNumber = <T>(value: T): AsNumber<T> => {
  if (value === undefined || value === null) return value as AsNumber<T>;
  if (typeof value === "number") return value as AsNumber<T>;
  const n = Number(value);
  if (Number.isNaN(n)) return value as AsNumber<T>;
  return n as AsNumber<T>;
};

// Unlike asNumber, returns undefined (never the original value) for anything that isn't a valid
// positive integer -- for URL search params that must be either a real id or genuinely absent
// (e.g. accountId), so a malformed value is treated the same as "not there" rather than silently
// surviving as garbage.
export const asPositiveInt = (value: unknown): number | undefined => {
  if (typeof value !== "number" && typeof value !== "string") return undefined;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

export const isIterable = <T = any>(
  item: Iterable<T> | any,
): item is Iterable<T> => typeof item[Symbol.iterator] === "function";

type AsArray<T> = T extends null | undefined
  ? T
  : T extends readonly any[]
    ? T
    : T extends string
      ? T[]
      : T extends Iterable<infer U>
        ? U[]
        : T[];
export function asArray<T>(value: T): AsArray<T> {
  if (value === null || value === undefined) {
    return value as AsArray<T>;
  }

  if (Array.isArray(value)) {
    return value as AsArray<T>;
  }

  if (typeof value !== "string" && isIterable(value)) {
    return [...value] as AsArray<T>;
  }

  return [value] as any;
}

export const functionalUpdateMap = <T, K>(
  a: Updater<T>,
  value: K,
  forward: (k: K) => T,
  backward: (t: T) => Partial<K>,
): K => {
  // const before = value;
  const after = { ...value, ...backward(functionalUpdate(a, forward(value))) };
  return after;
};

// export const asTablePagination = (query, setQuery) =>
export const isDefined = <T>(v: T | undefined | null) =>
  v !== undefined && v !== null;

export const clamp = <T>(
  v: T,
  min: T | undefined = undefined,
  max: T | undefined = undefined,
) => (isDefined(min) && min > v ? min : isDefined(max) && max < v ? max : v);

export const step = (
  value: number,
  input: [number, number],
  output: [number, number],
) => {
  const [min, max] = input;
  const [omin, omax] = output;
  return ((value - min) / (max - min)) * (omax - omin) + omin;
};

export const stepClamp = (
  value: number,
  input: [number, number],
  output: [number, number],
) => {
  const inClamp =
    input[0] < input[1]
      ? [input[0], input[1]]
      : ([input[1], input[0]] as [number, number]);
  const outClamp =
    output[0] < output[1]
      ? [output[0], output[1]]
      : ([output[1], output[0]] as [number, number]);
  return clamp(step(clamp(value, ...inClamp), input, output), ...outClamp);
};

export const toCamelCase = (str: string) => {
  return str
    .replace(/[-_](.)/g, (_, char) => char.toUpperCase())
    .replace(/^[a-z]/, (char) => char.toUpperCase());
};

export const asStateProperty = <TState, TProperty extends keyof TState>(
  [state, setState]: WithSetter<TState>,
  property: TProperty,
  additional?: Partial<TState>,
) =>
  [
    state?.[property],
    (a: Updater<TState[TProperty]>) =>
      setState((s) => ({
        ...s,
        ...additional,
        [property]: functionalUpdate(a, s[property]),
      })),
  ] as WithSetter<TState[TProperty]>;

export const asStateSelection = <
  TState,
  TStateSelected extends Partial<TState>,
>(
  [state, setState]: WithSetter<TState>,
  selector: (s: TState) => TStateSelected,
) =>
  [
    selector(state),
    (a: Updater<TStateSelected>) =>
      setState((s) => ({ ...s, ...functionalUpdate(a, selector(s)) })),
  ] as WithSetter<TStateSelected>;

export const flatKeys = <T extends FieldValues>(
  a?: T,
  parent?: FieldPath<T>,
): FieldPath<T>[] => {
  if ((typeof a === "object" && a !== null) || Array.isArray(a)) {
    return [
      ...(parent ? [parent] : []),
      ...(Object.entries(a).flatMap(([key, value]) =>
        flatKeys(value, parent ? `${parent}.${key}` : key),
      ) as FieldPath<T>[]),
    ];
  }
  return parent ? [parent] : [];
};

export const getError = (ex: any) => {
  let status;
  let message;
  if (isApiError(ex)) {
    status = ex?.status;
    message =
      ex?.body?.message ?? (ex?.body as any)?.error ?? (ex?.body as any);
  } else if (ex instanceof AxiosError) {
    status = ex?.response?.status ?? ex?.code;
    message =
      ex?.response?.data?.message ?? ex?.response?.data?.error ?? ex?.message;
  } else if (ex instanceof Error) {
    message = ex?.message;
  }
  return {
    status,
    message,
  };
};

export const filterSelf = <T>(item: T, filter: (item: T) => boolean) =>
  filter(item) ? item : undefined;

export const mapSelf = <T, K>(item: T, mapper: (t: T) => K) => {
  return mapper(item);
};

export const mapInPlace = <T>(
  items: T[],
  mapper: (t: T, index: number) => T | undefined,
) => {
  items.forEach(mapper);
  return items;
};

export const findMap = <T, K>(
  items: Iterable<T>,
  mapper: (t: T, index: number) => K | undefined,
) => {
  let i = 0;
  for (const item of items) {
    const found = mapper(item, i);
    if (found !== undefined) {
      return found;
    }
    i++;
  }
};

export function* mapFilterIterator<T, K>(
  items: Iterable<T>,
  mapper: (item: T, index: number) => K | undefined,
) {
  let i = 0;
  for (const item of items) {
    const next = mapper(item, i);
    if (next !== undefined) {
      yield next as K;
    }
    i++;
  }
}

export const mapFilter = <T, K>(
  items: Iterable<T>,
  mapper: (item: T, index: number) => K | undefined,
) => [...mapFilterIterator(items, mapper)];

export function* maxItemsIterator<T>(items: Iterable<T>, max: number) {
  let i = 0;
  for (const next of items) {
    if (i >= max) {
      break;
    }
    yield next;
    i++;
  }
}

export const maxItems = <T>(items: Iterable<T>, max: number) => [
  ...maxItemsIterator(items, max),
];

export const sleep = (t: number) => new Promise((res) => setTimeout(res, t));

export const renderBytes = (s?: number) => {
  if (!s || s < 0) return s;
  if (s > 1000000000) return `${(s / 1000000000).toFixed(2)} GB`;
  if (s > 1000000) return `${(s / 1000000).toFixed(2)} MB`;
  if (s > 1000) return `${(s / 1000).toFixed(2)} KB`;
  return `${s} Bytes`;
};

export const debug =
  (opts?: { name?: string; time?: boolean; call?: boolean }) =>
  <T extends (...args: any[]) => any>(f: T) => {
    const name = opts?.name ?? f?.name;
    let it = 0;
    return {
      [name]: (...args) => {
        const i = (it += 1);
        const n = `${name}#${i}`;
        if (opts?.time) console.time(name);
        if (opts?.call) console.log(`${n}(...`, args, `)`);
        const ret: any = f(...args);
        if (ret?.then) {
          ret.then((ret) => {
            console.log(`${n}(...`, args, `) =>`, ret);
            if (opts?.time) console.timeEnd(name);
          });
        } else {
          console.log(`${n}(...`, args, `) =>`, ret);
          if (opts?.time) console.timeEnd(name);
        }
        return ret;
      },
    }[name] as T;
  };

const veryLargeNumber = 9999999;
const largeNumber = 999999;

const getNextValue = (str: string | undefined): number => {
  if (!str) {
    return veryLargeNumber;
  }
  const [alpha, subVersion] = str.split(".");
  if (alpha.includes("alpha")) {
    return parseInt(subVersion);
  }
  return largeNumber;
};

export const compareSemVer = (a: string, b: string): 0 | 1 | -1 => {
  const [mainA, secondA, thirdA] = a.split("-");
  const [mainB, secondB, thirdB] = b.split("-");

  const mainCompare = compareBuild(coerce(mainB)!, coerce(mainA)!);
  if (mainCompare !== 0) return mainCompare;

  const secondAValue = getNextValue(secondA);
  const secondBValue = getNextValue(secondB);
  if (secondAValue > secondBValue) return -1;
  if (secondAValue < secondBValue) return 1;

  if (secondAValue === secondBValue) {
    if (secondAValue === largeNumber) {
      const secondCompare = secondB.localeCompare(secondA);
      if (secondCompare !== 0) {
        if (secondCompare > 0) return 1;
        if (secondCompare < 0) return -1;
      }
    }

    const thirdAValue = getNextValue(thirdA);
    const thirdBValue = getNextValue(thirdB);
    if (thirdAValue > thirdBValue) return -1;
    if (thirdAValue < thirdBValue) return 1;

    if (thirdAValue === largeNumber) {
      const thirdCompare = thirdB.localeCompare(thirdA);
      if (thirdCompare > 0) return 1;
      if (thirdCompare < 0) return -1;
    }
  }
  return 0;
};

export const copyToClipboard = async (text: string) => {
  try {
    if (navigator?.clipboard) {
      return await navigator.clipboard.writeText(text);
    }
  } catch (ex) {
    //
  }
  // https://stackoverflow.com/a/77480284
  // as navigator.clipboard is available only under secure context (https), this is a hack for local environments
  try {
    const input = document.createElement("textarea");
    input.value = text;
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
    return;
  } catch (ex) {
    //
  }
  // old browsers
  try {
    document.execCommand("copy", false, text);
    return;
  } catch (ex) {
    //
  }
};

export const clearForm = <
  T extends FieldValues,
  U extends FieldValues | undefined = undefined,
>(
  form: ReturnType<typeof useForm<T, U>>,
) => {
  const v = form.getValues();
  if (v === undefined || v === null || typeof v !== "object") {
    return;
  }
  const rec = (v: any, pk?: string) => {
    if (pk) {
      // console.log("clear", pk);
      form.setValue(pk as any, null as any);
    }
    if (!(typeof v === "object" && v !== null)) return;
    Object.entries(v).forEach(([k, v]) => rec(v, pk ? `${pk}.${k}` : k));
  };
  rec(form.getValues());
};

export const setForm = <
  T extends FieldValues,
  U extends FieldValues | undefined = undefined,
>(
  form: ReturnType<typeof useForm<T, U>>,
  v: T,
  clear?: boolean,
  options?: SetValueConfig,
) => {
  if (v === undefined || v === null || typeof v !== "object") {
    return;
  }
  const rec = (v: any, pk?: string) => {
    const isObject = typeof v === "object" && v !== null;
    const entries = isObject ? Object.entries(v) : [];
    if (!isObject || entries.length <= 0) {
      if (pk) {
        // console.log("set", pk, '"', v, '"');
        form.setValue(pk as any, v, options);
      }
      return;
    }
    if (clear) {
      if (pk) {
        form.setValue(pk as any, null as any, options);
      } else {
        form.reset({} as any);
      }
    }
    entries.forEach(([k, v]) => rec(v, pk ? `${pk}.${k}` : k));
  };
  rec(v);
};

type WatchObject<
  TFieldValues extends FieldValues,
  T extends Record<string, FieldPath<TFieldValues>>,
> = {
  [K in keyof T]: FieldPathValue<TFieldValues, T[K]>;
};

export const useFormWatch = <
  TFieldValues extends FieldValues,
  WatchMap extends Record<string, FieldPath<TFieldValues>>,
>(
  form: UseFormReturn<TFieldValues>,
  items: WatchMap,
) => {
  const { values, gather } = React.useMemo(
    () => {
      const entries = Object.entries(items);
      const values = entries.map(([_, values]) => values as any);
      const gather = (items: any[]) => {
        let isDefined = false;
        const value = Object.fromEntries(
          items.map((item, i) => {
            isDefined = isDefined || item !== undefined;
            return [entries[i][0], item];
          }),
        );
        return isDefined ? value : undefined;
      };
      return { values, gather };
    },
    [
      /*items*/
    ],
  );
  const watched = useWatch({ name: values, control: form.control });
  const value = React.useMemo(
    () => gather(watched),
    [gather, watched],
  ) as WatchObject<TFieldValues, WatchMap>;
  return value;
};

export const useFormWatchSetter = <
  TFieldValues extends FieldValues,
  WatchMap extends Record<string, FieldPath<TFieldValues>>,
>(
  form: UseFormReturn<TFieldValues>,
  items: WatchMap,
  options?: SetValueConfig,
) => {
  const optionsRef = useCurrentRef(options);
  return React.useCallback(
    (obj: WatchObject<TFieldValues, WatchMap>, options?: SetValueConfig) => {
      Object.entries(obj).map(([k, v]) =>
        form.setValue(
          items[k],
          v,
          optionsRef.current && options
            ? { ...optionsRef.current, ...options }
            : (options ?? optionsRef.current),
        ),
      );
    },
    [form /*, items*/],
  );
};

export const useFormWatchDefined = <
  TFieldValues extends FieldValues,
  WatchMap extends Record<string, FieldPath<TFieldValues>>,
>(
  value: WatchObject<TFieldValues, WatchMap>,
) => {
  return React.useMemo(
    () => Object.values(value).find((v) => v !== undefined),
    [value],
  );
};

export const useFormWatchState = <
  TFieldValues extends FieldValues,
  WatchMap extends Record<string, FieldPath<TFieldValues>>,
>(
  form: UseFormReturn<TFieldValues>,
  items: WatchMap,
  options?: SetValueConfig,
) => {
  const value = useFormWatch(form, items);
  const setter = useFormWatchSetter(form, items, options);
  return React.useMemo(() => [value, setter] as const, [value, setter]);
};

export const useFormFieldWatchState = <
  TFieldValues extends FieldValues,
  TField extends FieldPath<TFieldValues>,
>(
  form: UseFormReturn<TFieldValues>,
  field: TField,
  options?: SetValueConfig,
) => {
  const optionsRef = useCurrentRef(options);
  const value = useWatch({ name: field, control: form.control });
  const setter = React.useCallback(
    (value: PathValue<TFieldValues, TField>, options?: SetValueConfig) =>
      form.setValue(
        field,
        value,
        optionsRef.current && options
          ? { ...optionsRef.current, ...options }
          : (options ?? optionsRef.current),
      ),
    [form, field],
  );
  return React.useMemo(() => [value, setter] as const, [value, setter]);
};

export const compare = {
  shallow: (options?: {
    excluding?: string[];
    including?: string[];
    mode?: "both" | "a" | "b";
    mapper?: (t: any) => any;
    debug?: any;
  }) => {
    const { excluding, including, mode = "both", mapper } = options ?? {};
    return <T extends object>(_a: T, _b: T) => {
      const debug =
        options?.debug ??
        (_a as { debug?: any })?.debug ??
        (_b as { debug?: any })?.debug;
      const a = mapper ? mapper(_a) : _a;
      const b = mapper ? mapper(_b) : _b;
      if (mode === "both" || mode === "a") {
        for (const key in a)
          if (
            (excluding ? !excluding.includes(key) : true) &&
            (including ? including.includes(key) : true) &&
            (!(key in b) || a[key] !== b[key])
          ) {
            if (debug) {
              console.debug(
                `${typeof debug === "boolean" ? "" : `${debug} `}a: key ${key} changed, ${a?.[key]} -> ${b?.[key]}`,
                {
                  a,
                  b,
                },
              );
            }
            return false;
          }
      }
      if (mode === "both" || mode === "b") {
        for (const key in b)
          if (
            (excluding ? !excluding.includes(key) : true) &&
            (including ? including.includes(key) : true) &&
            (!(key in a) || a[key] !== b[key])
          ) {
            if (debug) {
              console.debug(
                `${typeof debug === "boolean" ? "" : `${debug} `}b: key ${key} changed, ${a?.[key]} -> ${b?.[key]}`,
                {
                  a,
                  b,
                },
              );
            }
            return false;
          }
      }
      // if (debug) {
      //   console.debug(
      //     `${typeof debug === "boolean" ? "" : `${debug} `} a shallow equal b`,
      //     {
      //       a,
      //       b,
      //     },
      //   );
      // }
      return true;
    };
  },
  and: <T extends object>(...steps: ((a: T, b: T) => boolean)[]) => {
    const rec = (steps: ((a: T, b: T) => boolean)[], a: T, b: T) =>
      steps.length <= 0 ? true : steps[0](a, b) && rec(steps.slice(1), a, b);
    return (a: T, b: T) => rec(steps, a, b);
  },
};

export const renderBool = (b?: boolean) =>
  b !== undefined ? (b ? "true" : "false") : undefined;

type CallAll = {
  <T extends (...args: any[]) => any>(
    ...args: (T | undefined)[]
  ): T | undefined;
  <_T extends (...args: any[]) => any>(...args: undefined[]): undefined;
  <T extends (...args: any[]) => any>(...args: T[]): T;
};

export const callAll = (<T extends (...args: any[]) => any>(
  ...args: (T | undefined)[]
) => {
  const functions = args.filter(Boolean) as T[];
  if (functions.length <= 0) return undefined;
  return ((...args: any[]) => functions.forEach((f) => f(...args))) as T;
}) as CallAll;

type FirstNotUndefined = {
  <T>(...args: (T | null | undefined)[]): T | null | undefined;
  <T>(...args: (T | undefined)[]): T | undefined;
  <T>(...args: (T | null)[]): T | null;
  <T>(...args: T[]): T;
};

export const firstNotUndefined = (<T>(...args: (T | null | undefined)[]) => {
  for (const arg of args) {
    if (arg !== undefined) {
      return arg;
    }
  }
  return undefined;
}) as FirstNotUndefined;

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const downloadFile = async (url: string) => {
  const link = document.createElement("a");
  link.style.display = "none";
  link.setAttribute("href", url);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  await delay(700);
};

export const mod = (n: number, m: number) => {
  const remain = n % m;
  return remain < 0 ? remain + m : remain;
};
export const min = (...values: (number | undefined | null)[]) => {
  let min: number | undefined = undefined;
  for (const v of values) {
    if (!isDefined(v)) continue;
    if (!isDefined(min) || v < min) {
      min = v;
    }
  }
  return min;
};

export type Renderable<P = {}> = React.FC<P> | React.ReactNode;
export const render = <P extends {}>(
  item: Renderable<P>,
  props: P = {} as P,
) =>
  typeof item === "function"
    ? React.createElement(item, props) // <item {...props} />
    : item;

// Never name a module export `then`: it makes the module's namespace a thenable, so any
// `await import()` of this file -- which is how Vitest's module runner loads every module -- calls it
// with the promise's resolve/reject, and the import rejects with the resolve function itself.
export const whenResolved = <T>(
  p: T | Promise<T>,
  cb: ((p: T) => any) | undefined,
) => {
  if (!cb) return;
  if (typeof (p as Promise<T>)?.then == "function") {
    return (p as Promise<T>).then(cb);
  }
  return cb(p as T);
};

export const max = (...values: (number | undefined | null)[]) => {
  let max: number | undefined = undefined;
  for (const v of values) {
    if (!isDefined(v)) continue;
    if (!isDefined(max) || v > max) {
      max = v;
    }
  }
  return max;
};

type Inclusive = { startDate?: true; endDate?: true };

export const earliest = (
  dates: (Date | undefined | null)[],
  options?: {
    startDate?: Date;
    endDate?: Date;
    inclusive?: Inclusive | true;
  },
) => {
  let min: Date | undefined = undefined;
  for (const v of dates) {
    if (!isDefined(v)) continue;
    if (
      (!isDefined(min) || isAfter(min, v)) &&
      (options?.startDate
        ? ((options?.inclusive as Inclusive)?.startDate ||
          options?.inclusive === true
            ? isEqual(v, options.startDate)
            : false) || isAfter(v, options.startDate)
        : true) &&
      (options?.endDate
        ? ((options?.inclusive as Inclusive)?.endDate ||
          options?.inclusive === true
            ? isEqual(v, options.endDate)
            : false) || isAfter(options.endDate, v)
        : true)
    ) {
      min = v;
      //
    }
  }
  return min;
};

export const latest = (
  dates: (Date | undefined | null)[],
  options?: {
    startDate?: Date;
    endDate?: Date;
    inclusive?: Inclusive | true;
  },
) => {
  let max: Date | undefined = undefined;
  for (const v of dates) {
    if (!isDefined(v)) continue;
    if (
      (!isDefined(max) || isAfter(v, max)) &&
      (options?.startDate
        ? ((options?.inclusive as Inclusive)?.startDate ||
          options.inclusive === true
            ? isEqual(v, options.startDate)
            : false) || isAfter(v, options.startDate)
        : true) &&
      (options?.endDate
        ? ((options?.inclusive as Inclusive)?.endDate ||
          options.inclusive === true
            ? isEqual(v, options.endDate)
            : false) || isAfter(options.endDate, v)
        : true)
    ) {
      max = v;
      //
    }
  }
  return min;
};

export type ColumnCell<TData, TValue = any> = ColumnDefTemplate<
  CellContext<TData, TValue>
>;

export type ColumnHeader<TData, TValue = any> = ColumnDefTemplate<
  HeaderContext<TData, TValue>
>;
export type ColumnFooter<TData, TValue = any> = ColumnDefTemplate<
  HeaderContext<TData, TValue>
>;

export type CommonQueryOptions<T> = UseQueryOptions<
  T extends UseQueryOptions<infer A, any, any, any> ? A : never,
  T extends UseQueryOptions<any, infer B, any, any> ? B : never,
  T extends UseQueryOptions<any, any, infer C, any> ? C : never,
  T extends UseQueryOptions<any, any, any, infer D> ? D : never
>;

export function* mapChildrenIterator<T extends Element, K>(
  col: HTMLCollection,
  mapper: (el: T) => K,
) {
  for (let i = 0; i <= col.length; i++) {
    const child = col.item(i);
    if (!child) return;
    yield mapper(child as T);
  }
}

export function mapChildren<T extends Element, K>(
  col: HTMLCollection,
  mapper: (el: T) => K,
) {
  return [...mapChildrenIterator(col, mapper)];
}

export const aucToPercent = (auc?: number) =>
  auc == null ? "N/A" : `${Number((auc * 100).toFixed(2))}%`;

export const countPretty = (count?: number) => count?.toLocaleString("en-US");

export const naturalSort = (a: string | number, b: string | number) =>
  `${a}`.localeCompare(`${b}`, undefined, {
    numeric: true,
    sensitivity: "base",
  });

function extractDirty<T extends FieldValues>(
  dirty: Partial<Readonly<DeepMap<DeepPartial<T>, boolean>>>,
  values: T,
): PartialDeep<T> | undefined {
  if (!isDefined(dirty)) {
    return undefined;
  }
  const entries = mapFilter(Object.entries(dirty), ([key, isDirtyOrNested]) => {
    const value = values[key];
    return isDirtyOrNested === true
      ? [key, value]
      : typeof isDirtyOrNested === "object"
        ? [
            key,
            value === undefined
              ? undefined
              : value === null
                ? null
                : // RHF flags array dirtiness per index; recursing would turn the
                  // array into an index-keyed object holding only the dirty
                  // entries, so send the whole current array instead.
                  Array.isArray(value)
                  ? value
                  : extractDirty(isDirtyOrNested, value),
          ]
        : undefined;
  });
  if (entries.length <= 0) {
    return undefined;
  }
  return Object.fromEntries(entries);
}

// Function to get dirty fields from form in the same structure
export function getDirty<T extends FieldValues>(form: UseFormReturn<T>) {
  return extractDirty(form.formState.dirtyFields, form.getValues());
}

export const NIL = Symbol("nil");
export const notNil = <Value>(v: Value, _default?: Value) =>
  (v === NIL ? _default : v) as Exclude<Value, typeof NIL>;
export const extractNotNil = <Value>(value: Value, _default?: any) =>
  Array.isArray(value)
    ? value.map((v) => extractNotNil(v, _default))
    : typeof value === "object" && value !== null
      ? Object.fromEntries(
          Object.entries(value).map(([k, v]) => [
            k,
            extractNotNil(v, _default),
          ]),
        )
      : notNil(value, _default);

export function assign<T extends Func, K extends PartialDeep<Parameters<T>[0]>>(
  action: T,
  data: K,
) {
  return (
    arg: MergeDeep<Exclude<Parameters<T>[0], undefined>, PartialDeep<K>>,
  ) => action(_.merge({}, data, arg));
}

/**
 * Parse a numeric text-input value into an integer form value: empty string
 * clears the field (undefined), anything else is rounded to a whole number.
 * Shared by the account-overview capacity/percentage inputs.
 */
export const parseIntInput = (value: string): number | undefined =>
  value === "" ? undefined : Math.round(Number(value));
